import {
  toWireIncentive,
  type ReviewAnswer,
  type ReviewAskView,
} from "../../lib/review-ask";
import { ReviewCloseUpgradeModal } from "./ReviewCloseUpgradeModal";
import { WorkedTogetherReviewSheet } from "./WorkedTogetherReviewSheet";

/**
 * ReviewAskDialog — opens the ONE sheet the server's ask calls for
 * (`reviewAskView` over `next_ask`): the worked_together capture sheet, or the
 * close-upgrade modal pre-filled from the existing review. Every buyer surface
 * that asks for a review mounts this rather than choosing a sheet itself, so
 * the two stages can never be offered out of order or twice.
 *
 * Renders nothing for a `readonly`/`none` view: an answered ask has no form.
 */
export interface ReviewAskDialogProps {
  view: ReviewAskView;
  open: boolean;
  proName: string;
  weeksTogether?: number;
  incentiveLabel?: string;
  /** Close-modal headline/subtext. Defaults to the generic closing copy; the
   *  check-in card passes its own congratulations. */
  congratsHeadline?: string;
  congratsSubtext?: string;
  onClose: () => void;
  onSubmit: (answer: ReviewAnswer) => void;
}

const DEFAULT_CLOSE_HEADLINE = "🎉 Congratulations on closing!";
const DEFAULT_CLOSE_SUBTEXT = "Tell other buyers how it ended.";

export function ReviewAskDialog({
  view,
  open,
  proName,
  weeksTogether,
  incentiveLabel,
  congratsHeadline = DEFAULT_CLOSE_HEADLINE,
  congratsSubtext = DEFAULT_CLOSE_SUBTEXT,
  onClose,
  onSubmit,
}: ReviewAskDialogProps) {
  if (view.kind === "capture") {
    return (
      <WorkedTogetherReviewSheet
        agentName={proName}
        onClose={onClose}
        onSubmit={({ stars, body }) => onSubmit({ stars, body })}
        open={open}
        weeksTogether={weeksTogether}
      />
    );
  }
  if (view.kind === "close") {
    return (
      <ReviewCloseUpgradeModal
        agentName={proName}
        confirmCloseRequired={view.confirmRequired}
        congratsHeadline={congratsHeadline}
        congratsSubtext={congratsSubtext}
        incentiveLabel={incentiveLabel}
        initialBody={view.prefill?.body}
        initialStars={view.prefill?.stars}
        onClose={onClose}
        onSubmit={(result) =>
          onSubmit({
            stars: result.stars,
            body: result.body,
            incentiveHonored: toWireIncentive(result.incentiveHonored),
            confirmClosed: result.confirmClosed,
          })
        }
        open={open}
      />
    );
  }
  return null;
}
